import React, { useState, useEffect, useMemo } from 'react';
import { format, isToday, isTomorrow, isThisWeek, subDays, addDays } from 'date-fns';
import { el } from 'date-fns/locale';
import { bookingApi, staffApi } from '../services/api';
import { useAppLayout } from '../components/AppLayout';
import BookingModal from '../components/BookingModal';
import './BookingPage.css';

const STATUS_LABELS = {
  confirmed:  { label: 'Επιβεβαιωμένη', color: '#0F6E56', bg: '#ECFDF5' },
  pending:    { label: 'Εκκρεμής',       color: '#92400E', bg: '#FFFBEB' },
  cancelled:  { label: 'Ακυρώθηκε',      color: '#991B1B', bg: '#FEF2F2' },
  completed:  { label: 'Ολοκληρώθηκε',   color: '#1e40af', bg: '#EFF6FF' },
  no_show:    { label: 'Δεν ήρθε',        color: '#6B21A8', bg: '#FAF5FF' },
};

const FILTERS = [
  { id: 'all',       label: 'Όλες' },
  { id: 'today',     label: 'Σήμερα' },
  { id: 'tomorrow',  label: 'Αύριο' },
  { id: 'week',      label: 'Εβδομάδα' },
  { id: 'past',      label: 'Παρελθόν' },
];

// ── Βοηθητική: επιστρέφει την effective τιμή κράτησης ──────────
// discount παρακάμπτει service.price
const effectivePrice = (b) => {
  if (b.discount != null && parseFloat(b.discount) > 0) return parseFloat(b.discount);
  if (b.service?.price) return parseFloat(b.service.price);
  return null;
};

export default function BookingPage() {
  const { openSidebar } = useAppLayout();

  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [staffList, setStaffList]     = useState([]);
  const [search, setSearch]           = useState('');
  const [filter, setFilter]           = useState('all');
  const [staffFilter, setStaffFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editBooking, setEditBooking] = useState(null);
  const [showModal, setShowModal]     = useState(false);

  useEffect(() => {
    setLoading(true);
    const from = subDays(new Date(), 60).toISOString();
    const to   = addDays(new Date(), 60).toISOString();
    bookingApi.getAll(from, to)
      .then(r => setAllBookings(r.data.bookings || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    staffApi.getAll().then(r => setStaffList(r.data.staff || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    let list = [...allBookings];
    const now = new Date();
    if (filter === 'today')    list = list.filter(b => isToday(new Date(b.starts_at)));
    if (filter === 'tomorrow') list = list.filter(b => isTomorrow(new Date(b.starts_at)));
    if (filter === 'week')     list = list.filter(b => isThisWeek(new Date(b.starts_at), { weekStartsOn: 1 }));
    if (filter === 'past')     list = list.filter(b => new Date(b.starts_at) < now);
    if (staffFilter !== 'all') list = list.filter(b => b.staff_id === staffFilter);
    if (statusFilter !== 'all') list = list.filter(b => b.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(b =>
        b.client_name?.toLowerCase().includes(q) ||
        b.client_phone?.toLowerCase().includes(q) ||
        b.service_name?.toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at));
  }, [allBookings, filter, staffFilter, statusFilter, search]);

  const handleEdit = (b) => { setEditBooking(b); setShowModal(true); };

  const handleSave = async (data) => {
    if (data.id) {
      const res = await bookingApi.update(data.id, data);
      setAllBookings(prev => prev.map(b => b.id === data.id ? res.data.booking : b));
    }
    setShowModal(false); setEditBooking(null);
  };

  const handleDelete = async (id) => {
    await bookingApi.cancel(id);
    setAllBookings(prev => prev.map(b => b.id === id ? { ...b, status: 'cancelled' } : b));
    setShowModal(false); setEditBooking(null);
  };

  const stats = useMemo(() => {
    const today     = allBookings.filter(b => isToday(new Date(b.starts_at)) && b.status !== 'cancelled');
    const confirmed = allBookings.filter(b => b.status === 'confirmed');
    const week      = allBookings.filter(b => isThisWeek(new Date(b.starts_at), { weekStartsOn: 1 }) && b.status !== 'cancelled');
    return { today: today.length, confirmed: confirmed.length, week: week.length, total: allBookings.length };
  }, [allBookings]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(b => {
      const key = format(new Date(b.starts_at), 'yyyy-MM-dd');
      if (!groups[key]) groups[key] = [];
      groups[key].push(b);
    });
    return Object.entries(groups).sort(([a], [b]) => b.localeCompare(a));
  }, [filtered]);

  const chipColor = (b) => b.staff?.color || '#534AB7';

  const dayLabel = (dateStr) => {
    const d = new Date(dateStr);
    if (isToday(d))    return 'Σήμερα';
    if (isTomorrow(d)) return 'Αύριο';
    return format(d, 'EEEE, d MMMM yyyy', { locale: el });
  };

  return (
    <div className="bp-page">
      {/* ── Header ────────────────────────────────────────────── */}
      <div className="bp-header">
        <div className="bp-header-top">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="hamburger-btn" onClick={openSidebar}>☰</button>
            <h1 className="bp-title">📋 Κρατήσεις</h1>
          </div>
          <div className="bp-stats">
            <div className="bp-stat"><span className="bp-stat-val">{stats.today}</span><span className="bp-stat-lbl">Σήμερα</span></div>
            <div className="bp-stat"><span className="bp-stat-val">{stats.week}</span><span className="bp-stat-lbl">Εβδομάδα</span></div>
            <div className="bp-stat"><span className="bp-stat-val">{stats.confirmed}</span><span className="bp-stat-lbl">Επιβεβ.</span></div>
            <div className="bp-stat"><span className="bp-stat-val">{stats.total}</span><span className="bp-stat-lbl">Σύνολο</span></div>
          </div>
        </div>

        <div className="bp-search-row">
          <div className="bp-search-wrap">
            <span className="bp-search-icon">🔍</span>
            <input className="bp-search" placeholder="Αναζήτηση πελάτη, τηλέφωνο, υπηρεσία..."
              value={search} onChange={e => setSearch(e.target.value)} />
            {search && <button className="bp-search-clear" onClick={() => setSearch('')}>×</button>}
          </div>
        </div>

        <div className="bp-filters-row">
          <div className="bp-filter-group">
            {FILTERS.map(f => (
              <button key={f.id} className={'bp-filter-btn' + (filter === f.id ? ' active' : '')}
                onClick={() => setFilter(f.id)}>{f.label}</button>
            ))}
          </div>
          <div className="bp-filter-selects">
            {staffList.length > 0 && (
              <select className="bp-select" value={staffFilter} onChange={e => setStaffFilter(e.target.value)}>
                <option value="all">Όλοι οι υπάλληλοι</option>
                {staffList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            <select className="bp-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="all">Όλες οι καταστάσεις</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── Content ───────────────────────────────────────────── */}
      <div className="bp-content">
        {loading && <div className="bp-empty"><div className="bp-spinner" /><p>Φόρτωση...</p></div>}

        {!loading && filtered.length === 0 && (
          <div className="bp-empty">
            <div className="bp-empty-icon">📭</div>
            <p className="bp-empty-title">Δεν βρέθηκαν κρατήσεις</p>
            <p className="bp-empty-sub">Δοκίμασε διαφορετικά φίλτρα ή αναζήτηση</p>
          </div>
        )}

        {!loading && grouped.map(([dateKey, items]) => (
          <div key={dateKey} className="bp-day-group">
            <div className="bp-day-label">
              <span className="bp-day-text">{dayLabel(dateKey)}</span>
              <span className="bp-day-count">{items.length} κράτηση{items.length !== 1 ? 'εις' : ''}</span>
            </div>
            <div className="bp-list">
              {items.map(b => {
                const st      = STATUS_LABELS[b.status] || STATUS_LABELS.confirmed;
                const color   = chipColor(b);
                const endDt   = new Date(new Date(b.starts_at).getTime() + b.service_duration * 60000);
                const effPrice = effectivePrice(b);
                const hasDiscount = b.discount != null && parseFloat(b.discount) > 0
                  && b.service?.price && parseFloat(b.service.price) !== parseFloat(b.discount);

                return (
                  <div key={b.id} className={'bp-card' + (b.status === 'cancelled' ? ' bp-card-cancelled' : '')}
                    onClick={() => b.status !== 'cancelled' && handleEdit(b)}
                    style={{ '--card-color': color }}>
                    <div className="bp-card-bar" style={{ background: color }} />
                    <div className="bp-card-body">
                      <div className="bp-card-top">
                        <div className="bp-card-client">
                          <div className="bp-card-avatar" style={{ background: color + '22', color }}>
                            {b.client_name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div>
                            <div className="bp-card-name">{b.client_name}</div>
                            {b.client_phone && <div className="bp-card-phone">📞 {b.client_phone}</div>}
                          </div>
                        </div>
                        <span className="bp-status-badge" style={{ color: st.color, background: st.bg }}>{st.label}</span>
                      </div>
                      <div className="bp-card-mid">
                        <div className="bp-card-service">✂️ {b.service_name || '—'}</div>
                        <div className="bp-card-time">
                          🕐 {format(new Date(b.starts_at), 'HH:mm')} – {format(endDt, 'HH:mm')}
                          <span className="bp-card-dur">({b.service_duration} λεπτά)</span>
                        </div>

                        {/* ── Τιμή με discount ── */}
                        {effPrice != null && (
                          <div className="bp-card-price">
                            💶{' '}
                            {hasDiscount ? (
                              <>
                                <span style={{ textDecoration: 'line-through', opacity: 0.45, marginRight: 4, fontWeight: 400 }}>
                                  {parseFloat(b.service.price).toFixed(2)}€
                                </span>
                                <span style={{ color: '#dc2626' }}>{effPrice.toFixed(2)} €</span>
                                <span style={{
                                  marginLeft: 4, fontSize: 10, background: '#fee2e2',
                                  color: '#dc2626', borderRadius: 4, padding: '1px 5px', fontWeight: 600
                                }}>
                                  −{(parseFloat(b.service.price) - effPrice).toFixed(2)}€
                                </span>
                              </>
                            ) : (
                              `${effPrice.toFixed(2)} €`
                            )}
                          </div>
                        )}

                        {b.staff && (
                          <div className="bp-card-staff">
                            <span className="bp-staff-dot" style={{ background: b.staff.color }} />
                            {b.staff.name}
                            {b.staff.specialty && <span className="bp-card-spec"> · {b.staff.specialty}</span>}
                          </div>
                        )}
                      </div>
                      {b.notes && <div className="bp-card-notes">💬 {b.notes}</div>}
                    </div>
                    {b.status !== 'cancelled' && <div className="bp-card-arrow">›</div>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <BookingModal existingBooking={editBooking} schedule={null}
          onSave={handleSave} onDelete={handleDelete}
          onClose={() => { setShowModal(false); setEditBooking(null); }} />
      )}
    </div>
  );
}